/**
 * v4.6.0 — YUL On Tap — Operational Intelligence Login.
 *
 * Split-screen enterprise login:
 *   LEFT  — YUL emblem · Welcome Back · auth form · SSO · security footer
 *   RIGHT — Operational-truth hero · 4 KPI cards · Sentiment trend chart ·
 *            Live Intelligence Feed · "Protect Your Brand" CTA card ·
 *            compliance footer
 *
 * Constitutional notes:
 *   * All copy is i18n — never hardcoded literals (EN / FR / ES).
 *   * The right-side intelligence panel is live HTML + a real Recharts
 *     line chart — not a flattened image — so translations and future
 *     data hooks slot in cleanly.
 *   * Subtle ambient motion only: a 3.5s soft pulse on the "Live" dot
 *     and on the KPI card hover glow, a calm chart entrance animation.
 *     No flashy transitions.
 *   * Mobile collapses the dashboard below the auth panel; auth always
 *     stays primary.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Toaster, toast } from "sonner";
import {
    Eye, EyeOff, Loader2, Lock, ChevronRight, ShieldCheck,
    Store, AlertTriangle, Clock, TrendingUp, Star, CheckCircle2,
    Shield, FileCheck2, Activity,
} from "lucide-react";
import {
    LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip,
    CartesianGrid,
} from "recharts";
import { useAuth } from "../lib/auth";
import { landingPathFor } from "../lib/landing";
import LanguageToggle from "../components/LanguageToggle";

// Synthetic 30-day sentiment trend (gentle upward curve, ends at 4.6).
const SENTIMENT_TREND = [
    { day: "May 11", value: 3.5 }, { day: "May 12", value: 3.5 },
    { day: "May 13", value: 3.5 }, { day: "May 14", value: 3.5 },
    { day: "May 15", value: 3.6 }, { day: "May 16", value: 3.6 },
    { day: "May 17", value: 3.6 }, { day: "May 18", value: 3.6 },
    { day: "May 19", value: 3.7 }, { day: "May 20", value: 3.7 },
    { day: "May 21", value: 3.8 }, { day: "May 22", value: 3.8 },
    { day: "May 23", value: 3.9 }, { day: "May 24", value: 3.9 },
    { day: "May 25", value: 4.0 }, { day: "May 26", value: 4.0 },
    { day: "May 27", value: 4.1 }, { day: "May 28", value: 4.1 },
    { day: "May 29", value: 4.2 }, { day: "May 30", value: 4.2 },
    { day: "May 31", value: 4.3 }, { day: "Jun 1",  value: 4.3 },
    { day: "Jun 2",  value: 4.3 }, { day: "Jun 3",  value: 4.4 },
    { day: "Jun 4",  value: 4.4 }, { day: "Jun 5",  value: 4.5 },
    { day: "Jun 6",  value: 4.5 }, { day: "Jun 7",  value: 4.5 },
    { day: "Jun 8",  value: 4.6 },
];

const KpiCard = ({ icon: Icon, accent, label, value, delta, sub, testId }) => (
    <div
        className="ob-login-kpi rounded-2xl p-4 sm:p-5"
        data-testid={testId}
        style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.015))",
            border: "1px solid rgba(255,255,255,0.07)",
            transition: "transform 200ms ease, border-color 200ms ease, box-shadow 300ms ease",
        }}
    >
        <div className="flex items-start gap-3 mb-3">
            <div
                className="flex items-center justify-center rounded-xl flex-shrink-0"
                style={{
                    width: 40, height: 40,
                    background: `${accent}1A`,
                    border: `1px solid ${accent}33`,
                }}
            >
                <Icon size={18} strokeWidth={1.75} style={{ color: accent }} />
            </div>
            <p
                className="text-xs leading-snug pt-1"
                style={{ color: "rgba(226, 232, 240, 0.70)" }}
            >
                {label}
            </p>
        </div>
        <div className="flex items-baseline gap-2 mb-1">
            <span
                className="text-3xl sm:text-4xl"
                style={{
                    color: "rgb(241, 245, 249)",
                    fontFamily: "Outfit, sans-serif",
                    fontWeight: 500,
                    letterSpacing: "-0.01em",
                }}
            >
                {value}
            </span>
            {delta && (
                <span
                    className="text-xs"
                    style={{
                        color: delta.direction === "up"
                            ? "rgb(74, 222, 128)"
                            : "rgb(74, 222, 128)",   // both deltas in screenshot use green
                        fontFamily: "Outfit, sans-serif",
                    }}
                >
                    {delta.direction === "up" ? "↑" : "↓"} {delta.value}
                </span>
            )}
        </div>
        {sub && (
            <p className="text-xs" style={{ color: "rgba(148, 163, 184, 0.70)" }}>
                {sub}
            </p>
        )}
    </div>
);

const FeedItem = ({ icon: Icon, accent, title, body, time, testId }) => (
    <div className="flex gap-3" data-testid={testId}>
        <div
            className="flex items-center justify-center rounded-full flex-shrink-0 mt-0.5"
            style={{
                width: 32, height: 32,
                background: `${accent}1A`,
                border: `1px solid ${accent}33`,
            }}
        >
            <Icon size={14} strokeWidth={1.75} style={{ color: accent }} />
        </div>
        <div className="min-w-0">
            <p
                className="text-sm leading-snug"
                style={{
                    color: accent,
                    fontFamily: "Outfit, sans-serif",
                    fontWeight: 500,
                }}
            >
                {title}
            </p>
            <p
                className="text-xs leading-relaxed mt-0.5"
                style={{ color: "rgba(203, 213, 225, 0.78)" }}
            >
                {body}
            </p>
            <p
                className="text-[11px] mt-1"
                style={{ color: "rgba(148, 163, 184, 0.55)" }}
            >
                {time}
            </p>
        </div>
    </div>
);

const SecurityBadge = ({ icon: Icon, label, testId }) => (
    <div
        className="inline-flex items-center gap-1.5"
        data-testid={testId}
        style={{ color: "rgba(148, 163, 184, 0.75)", fontSize: "0.78rem" }}
    >
        <Icon size={13} strokeWidth={1.75} />
        <span style={{ fontFamily: "Outfit, sans-serif" }}>{label}</span>
    </div>
);

const Login = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { login, isAuthenticated, user } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [remember, setRemember] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [explainerUrl, setExplainerUrl] = useState("");

    // v4.10.1 — Optional pre-auth "Watch a 30-second intro" link.
    // Reuses the existing per-org explainer URL infrastructure.
    useEffect(() => {
        const ctrl = new AbortController();
        // Runtime origin — see lib/api.js. Prevents stale preview URL from
        // being baked into the production bundle (caused CORS on portal.yulontap.com).
        const base = typeof window !== "undefined" ? window.location.origin : "";
        fetch(`${base}/api/setup-guide/public-explainer`, { signal: ctrl.signal })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (d?.onboarding_explainer_url) setExplainerUrl(d.onboarding_explainer_url); })
            .catch(() => { /* silent — link is fully optional */ });
        return () => ctrl.abort();
    }, []);

    useEffect(() => {
        if (isAuthenticated && user) navigate(landingPathFor(user));
    }, [isAuthenticated, user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error(t("errors.required", "Email and password required."));
            return;
        }
        setIsLoading(true);
        try {
            const userData = await login(email, password);
            toast.success(t("dashboard.welcome", "Welcome") + "!");
            // ONE RULE, one place: lib/landing.js. Fresh login and session
            // restore both go through it.
            navigate(landingPathFor(userData));
        } catch (error) {
            const message = error.response?.data?.detail
                || t("errors.loginFailed", "Login failed. Please try again.");
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen w-full"
            style={{
                background: "radial-gradient(ellipse at top right, #0B1828 0%, #060A14 55%, #03060C 100%)",
                color: "rgb(241, 245, 249)",
            }}
            data-testid="login-landing-root"
        >
            <Toaster position="top-right" richColors theme="dark" />

            {/* Language toggle — floats top-right on all viewports */}
            <div className="absolute top-5 right-5 z-50" data-testid="login-language-toggle">
                <LanguageToggle />
            </div>

            <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-[minmax(400px,38%)_1fr]">

                {/* ─── LEFT — Authentication panel ─────────────────────── */}
                <aside
                    className="flex flex-col justify-between px-6 sm:px-10 lg:px-12 py-10 lg:py-12"
                    style={{ background: "rgba(3, 6, 12, 0.72)" }}
                    data-testid="login-auth-panel"
                >
                    <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
                        {/* Logo */}
                        <div className="flex justify-center lg:justify-start mb-8">
                            <img
                                src="/yulontap-logo.png"
                                alt="YUL On Tap"
                                data-testid="login-yul-emblem"
                                style={{
                                    height: 168,
                                    width: "auto",
                                    objectFit: "contain",
                                    filter: "drop-shadow(0 8px 28px rgba(59, 130, 246, 0.25))",
                                }}
                            />
                        </div>

                        <h1
                            className="text-4xl sm:text-5xl mb-2"
                            data-testid="login-welcome-title"
                            style={{
                                fontFamily: "Outfit, sans-serif",
                                fontWeight: 600,
                                letterSpacing: "-0.015em",
                                color: "rgb(241, 245, 249)",
                            }}
                        >
                            {t("login_landing.welcome_back")}
                        </h1>
                        <p
                            className="mb-8 text-sm"
                            data-testid="login-welcome-subtitle"
                            style={{ color: "rgba(148, 163, 184, 0.85)" }}
                        >
                            {t("login_landing.sign_in_subtitle")}
                        </p>

                        <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                            {/* Email */}
                            <div>
                                <label
                                    htmlFor="login-email"
                                    className="block text-sm mb-2"
                                    style={{ color: "rgba(226, 232, 240, 0.90)", fontFamily: "Outfit, sans-serif" }}
                                >
                                    {t("login_landing.email_label")}
                                </label>
                                <input
                                    id="login-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={t("login_landing.email_placeholder")}
                                    autoComplete="email"
                                    data-testid="login-email-input"
                                    className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-colors"
                                    style={{
                                        background: "rgba(255,255,255,0.025)",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                        color: "rgb(241, 245, 249)",
                                    }}
                                />
                            </div>
                            {/* Password */}
                            <div>
                                <label
                                    htmlFor="login-password"
                                    className="block text-sm mb-2"
                                    style={{ color: "rgba(226, 232, 240, 0.90)", fontFamily: "Outfit, sans-serif" }}
                                >
                                    {t("login_landing.password_label")}
                                </label>
                                <div className="relative">
                                    <input
                                        id="login-password"
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder={t("login_landing.password_placeholder")}
                                        autoComplete="current-password"
                                        data-testid="login-password-input"
                                        className="w-full rounded-xl px-4 py-3 pr-11 text-sm outline-none transition-colors"
                                        style={{
                                            background: "rgba(255,255,255,0.025)",
                                            border: "1px solid rgba(255,255,255,0.08)",
                                            color: "rgb(241, 245, 249)",
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((s) => !s)}
                                        aria-label={t('login.togglePasswordVisibility')}
                                        data-testid="login-password-toggle"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/5"
                                        style={{ color: "rgba(148, 163, 184, 0.80)" }}
                                    >
                                        {showPassword
                                            ? <EyeOff size={16} strokeWidth={1.75} />
                                            : <Eye size={16} strokeWidth={1.75} />}
                                    </button>
                                </div>
                            </div>

                            {/* Remember + Forgot */}
                            <div className="flex items-center justify-between text-sm">
                                <label
                                    className="inline-flex items-center gap-2 cursor-pointer select-none"
                                    style={{ color: "rgba(226, 232, 240, 0.85)" }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={remember}
                                        onChange={(e) => setRemember(e.target.checked)}
                                        data-testid="login-remember-me"
                                        className="rounded"
                                        style={{ accentColor: "rgb(59, 130, 246)" }}
                                    />
                                    {t("login_landing.remember_me")}
                                </label>
                                <button
                                    type="button"
                                    onClick={() => navigate("/forgot-password")}
                                    data-testid="login-forgot-password"
                                    className="text-sm hover:underline transition-colors"
                                    style={{ color: "rgb(96, 165, 250)" }}
                                >
                                    {t("login_landing.forgot_password")}
                                </button>
                            </div>

                            {/* Sign In */}
                            <button
                                type="submit"
                                disabled={isLoading}
                                data-testid="login-submit-button"
                                className="w-full rounded-xl py-3 text-base flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                                style={{
                                    background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                                    color: "white",
                                    fontFamily: "Outfit, sans-serif",
                                    fontWeight: 500,
                                    boxShadow: "0 10px 28px rgba(37, 99, 235, 0.35)",
                                }}
                            >
                                {isLoading
                                    ? <><Loader2 size={16} className="animate-spin" /> {t("login_landing.signing_in")}</>
                                    : t("login_landing.sign_in")}
                            </button>

                            {/* Divider */}
                            <div className="flex items-center gap-3 my-2">
                                <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
                                <span className="text-xs" style={{ color: "rgba(148, 163, 184, 0.65)" }}>
                                    {t("login_landing.or_continue_with")}
                                </span>
                                <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
                            </div>

                            {/* SSO */}
                            <button
                                type="button"
                                onClick={() => toast.info("SSO is configured per-organization. Contact your administrator.")}
                                data-testid="login-sso-button"
                                className="w-full rounded-xl py-3 text-sm flex items-center justify-between px-5 transition-colors hover:bg-white/[0.03]"
                                style={{
                                    background: "rgba(255,255,255,0.02)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    color: "rgba(226, 232, 240, 0.92)",
                                    fontFamily: "Outfit, sans-serif",
                                }}
                            >
                                <span className="inline-flex items-center gap-2">
                                    <Lock size={14} strokeWidth={1.75} />
                                    {t("login_landing.sign_in_with_sso")}
                                </span>
                                <ChevronRight size={14} strokeWidth={1.75} style={{ opacity: 0.6 }} />
                            </button>
                        </form>
                    </div>

                    {/* Bottom strip: enterprise security + help */}
                    <div className="flex items-center justify-between gap-4 pt-8 mt-8 text-xs" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                        <div className="flex items-start gap-2 min-w-0">
                            <ShieldCheck size={14} strokeWidth={1.75} style={{ color: "rgba(148, 163, 184, 0.65)", marginTop: 2, flexShrink: 0 }} />
                            <div>
                                <p style={{ color: "rgba(226, 232, 240, 0.85)", fontFamily: "Outfit, sans-serif" }}>
                                    {t("login_landing.enterprise_security")}
                                </p>
                                <p style={{ color: "rgba(148, 163, 184, 0.65)" }}>
                                    {t("login_landing.enterprise_security_tagline")}
                                </p>
                            </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <p style={{ color: "rgba(148, 163, 184, 0.65)" }}>
                                {t("login_landing.need_help")}
                            </p>
                            <a
                                href="mailto:support@yulontap.com"
                                data-testid="login-contact-support"
                                className="hover:underline"
                                style={{ color: "rgb(96, 165, 250)" }}
                            >
                                {t("login_landing.contact_support")}
                            </a>
                            {explainerUrl && (
                                <a
                                    href={explainerUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="login-watch-intro"
                                    className="block mt-1 hover:underline"
                                    style={{ color: "rgba(148, 163, 184, 0.85)" }}
                                >
                                    {t("setup_guide.watch_intro")}
                                </a>
                            )}
                        </div>
                    </div>
                </aside>

                {/* ─── RIGHT — Operational Intelligence panel ──────────── */}
                <section
                    className="relative px-6 sm:px-10 lg:px-14 py-10 lg:py-12 overflow-hidden flex flex-col"
                    data-testid="login-intelligence-panel"
                >
                    {/* Ambient globe glow (subtle decorative layer) */}
                    <div
                        aria-hidden="true"
                        className="absolute top-0 right-0 pointer-events-none ob-login-globe"
                        style={{
                            width: 760, height: 760,
                            transform: "translate(28%, -22%)",
                            opacity: 0.55,
                            background: "radial-gradient(circle at 35% 35%, rgba(59, 130, 246, 0.22) 0%, rgba(59, 130, 246, 0.10) 38%, transparent 64%)",
                            borderRadius: "50%",
                            filter: "blur(2px)",
                        }}
                    />

                    {/* Hero */}
                    <header className="relative max-w-3xl mb-8 lg:mb-10">
                        <h2
                            data-testid="login-hero-title"
                            className="text-3xl sm:text-4xl lg:text-5xl mb-4"
                            style={{
                                fontFamily: "Outfit, sans-serif",
                                fontWeight: 600,
                                letterSpacing: "-0.02em",
                                lineHeight: 1.1,
                                color: "rgb(241, 245, 249)",
                            }}
                        >
                            {t("login_landing.hero_title")}
                        </h2>
                        <p
                            data-testid="login-hero-subtitle"
                            className="text-base sm:text-lg max-w-2xl"
                            style={{
                                color: "rgba(203, 213, 225, 0.82)",
                                lineHeight: 1.6,
                            }}
                        >
                            {t("login_landing.hero_subtitle")}
                        </p>
                    </header>

                    {/* KPI cards */}
                    <div
                        className="relative grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 lg:mb-8"
                        data-testid="login-kpi-grid"
                    >
                        <KpiCard
                            icon={Store} accent="#3B82F6"
                            label={t("login_landing.kpi_active_locations")}
                            value="124"
                            delta={{ direction: "up", value: "+8%" }}
                            sub={t("login_landing.kpi_vs_last_30")}
                            testId="login-kpi-active-locations"
                        />
                        <KpiCard
                            icon={AlertTriangle} accent="#F59E0B"
                            label={t("login_landing.kpi_open_incidents")}
                            value="3"
                            delta={{ direction: "down", value: "-25%" }}
                            sub={t("login_landing.kpi_vs_last_30")}
                            testId="login-kpi-open-incidents"
                        />
                        <KpiCard
                            icon={Clock} accent="#10B981"
                            label={t("login_landing.kpi_avg_resolution")}
                            value="18m"
                            delta={{ direction: "down", value: "-12%" }}
                            sub={t("login_landing.kpi_vs_last_30")}
                            testId="login-kpi-avg-resolution"
                        />
                        <KpiCard
                            icon={TrendingUp} accent="#A855F7"
                            label={t("login_landing.kpi_guest_sentiment")}
                            value="4.6"
                            delta={{ direction: "up", value: "+0.3" }}
                            sub={t("login_landing.kpi_out_of_5")}
                            testId="login-kpi-guest-sentiment"
                        />
                    </div>

                    {/* Chart + Feed */}
                    <div
                        className="relative grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4 lg:gap-5 mb-6 lg:mb-8"
                        data-testid="login-insight-grid"
                    >
                        {/* Sentiment chart */}
                        <div
                            className="rounded-2xl p-5 sm:p-6"
                            data-testid="login-sentiment-chart"
                            style={{
                                background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.015))",
                                border: "1px solid rgba(255,255,255,0.07)",
                                minHeight: 320,
                            }}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3
                                    className="text-base"
                                    style={{
                                        color: "rgb(241, 245, 249)",
                                        fontFamily: "Outfit, sans-serif",
                                        fontWeight: 500,
                                    }}
                                >
                                    {t("login_landing.chart_title")}
                                </h3>
                                <span
                                    className="text-xs px-2.5 py-1 rounded-full"
                                    style={{
                                        background: "rgba(255,255,255,0.04)",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                        color: "rgba(203, 213, 225, 0.85)",
                                    }}
                                >
                                    {t("login_landing.chart_range_30")}
                                </span>
                            </div>
                            <div style={{ width: "100%", height: 240 }}>
                                <ResponsiveContainer width="100%" height="100%" minHeight={240} debounce={1}>
                                    <LineChart data={SENTIMENT_TREND} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="sentimentGradient" x1="0" y1="0" x2="1" y2="0">
                                                <stop offset="0%" stopColor="#60A5FA" />
                                                <stop offset="100%" stopColor="#3B82F6" />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                                        <XAxis
                                            dataKey="day"
                                            tick={{ fill: "rgba(148, 163, 184, 0.65)", fontSize: 11 }}
                                            stroke="rgba(255,255,255,0.08)"
                                            ticks={["May 11", "May 18", "May 25", "Jun 1", "Jun 8"]}
                                        />
                                        <YAxis
                                            domain={[3.0, 5.0]}
                                            ticks={[3.0, 3.5, 4.0, 4.5, 5.0]}
                                            tick={{ fill: "rgba(148, 163, 184, 0.65)", fontSize: 11 }}
                                            stroke="rgba(255,255,255,0.08)"
                                            width={32}
                                        />
                                        <Tooltip
                                            cursor={{ stroke: "rgba(96, 165, 250, 0.30)", strokeWidth: 1 }}
                                            contentStyle={{
                                                background: "rgba(15, 23, 42, 0.95)",
                                                border: "1px solid rgba(96, 165, 250, 0.35)",
                                                borderRadius: 10,
                                                color: "rgb(241, 245, 249)",
                                                fontSize: 12,
                                            }}
                                            labelStyle={{ color: "rgba(203, 213, 225, 0.85)" }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            stroke="url(#sentimentGradient)"
                                            strokeWidth={2.2}
                                            dot={false}
                                            activeDot={{
                                                r: 5, fill: "#60A5FA",
                                                stroke: "rgba(255,255,255,0.20)", strokeWidth: 2,
                                            }}
                                            isAnimationActive={true}
                                            animationDuration={1100}
                                            animationEasing="ease-out"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Live feed */}
                        <div
                            className="rounded-2xl p-5 sm:p-6"
                            data-testid="login-live-feed"
                            style={{
                                background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.015))",
                                border: "1px solid rgba(255,255,255,0.07)",
                            }}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3
                                    className="text-base"
                                    style={{
                                        color: "rgb(241, 245, 249)",
                                        fontFamily: "Outfit, sans-serif",
                                        fontWeight: 500,
                                    }}
                                >
                                    {t("login_landing.feed_title")}
                                </h3>
                                <span
                                    className="inline-flex items-center gap-1.5 text-xs"
                                    style={{ color: "rgb(74, 222, 128)" }}
                                >
                                    <span className="ob-login-live-dot" />
                                    {t("login_landing.live_label")}
                                </span>
                            </div>
                            <div className="space-y-4">
                                <FeedItem
                                    icon={AlertTriangle} accent="#F59E0B"
                                    title={t("login_landing.feed_item_1_title")}
                                    body={t("login_landing.feed_item_1_body")}
                                    time={t("login_landing.feed_item_1_time")}
                                    testId="login-feed-item-1"
                                />
                                <FeedItem
                                    icon={Star} accent="#3B82F6"
                                    title={t("login_landing.feed_item_2_title")}
                                    body={t("login_landing.feed_item_2_body")}
                                    time={t("login_landing.feed_item_2_time")}
                                    testId="login-feed-item-2"
                                />
                                <FeedItem
                                    icon={CheckCircle2} accent="#10B981"
                                    title={t("login_landing.feed_item_3_title")}
                                    body={t("login_landing.feed_item_3_body")}
                                    time={t("login_landing.feed_item_3_time")}
                                    testId="login-feed-item-3"
                                />
                            </div>
                        </div>
                    </div>

                    {/* CTA strip */}
                    <div
                        className="relative rounded-2xl p-5 sm:p-6 flex items-start sm:items-center gap-5 flex-col sm:flex-row mb-6"
                        data-testid="login-protect-cta"
                        style={{
                            background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.015))",
                            border: "1px solid rgba(255,255,255,0.07)",
                        }}
                    >
                        <div
                            className="flex items-center justify-center rounded-2xl flex-shrink-0"
                            style={{
                                width: 48, height: 48,
                                background: "rgba(59, 130, 246, 0.10)",
                                border: "1px solid rgba(59, 130, 246, 0.30)",
                            }}
                        >
                            <Shield size={20} strokeWidth={1.75} style={{ color: "#60A5FA" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p
                                className="text-base mb-1"
                                style={{
                                    color: "rgb(241, 245, 249)",
                                    fontFamily: "Outfit, sans-serif",
                                    fontWeight: 500,
                                }}
                            >
                                {t("login_landing.cta_title")}
                            </p>
                            <p className="text-sm" style={{ color: "rgba(203, 213, 225, 0.78)", lineHeight: 1.55 }}>
                                {t("login_landing.cta_body")}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => document.getElementById("login-email")?.focus()}
                            data-testid="login-explore-cta"
                            className="inline-flex items-center gap-2 text-sm rounded-full px-5 py-2.5 transition-colors hover:bg-white/[0.04] flex-shrink-0"
                            style={{
                                color: "rgba(226, 232, 240, 0.92)",
                                background: "rgba(255,255,255,0.02)",
                                border: "1px solid rgba(255,255,255,0.10)",
                                fontFamily: "Outfit, sans-serif",
                            }}
                        >
                            {t("login_landing.cta_button")}
                            <ChevronRight size={14} strokeWidth={1.75} />
                        </button>
                    </div>

                    {/* Compliance footer */}
                    <div
                        className="relative flex flex-wrap items-center justify-center gap-x-8 gap-y-2 pt-3"
                        data-testid="login-compliance-footer"
                        style={{ borderTop: "1px solid rgba(255,255,255,0.05)", marginTop: "auto" }}
                    >
                        <SecurityBadge icon={Lock}      label={t("login_landing.sec_bank")}   testId="login-sec-bank" />
                        <SecurityBadge icon={FileCheck2} label={t("login_landing.sec_soc2")}   testId="login-sec-soc2" />
                        <SecurityBadge icon={Shield}    label={t("login_landing.sec_gdpr")}   testId="login-sec-gdpr" />
                        <SecurityBadge icon={Activity}  label={t("login_landing.sec_uptime")} testId="login-sec-uptime" />
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Login;
